import { useState } from "react";
import { useResponsibleQuery } from "../../hooks/useResponsibleQuery";
import { TransferUserResponsibilityModel } from "../TransferUserResponsibility.types";
import { post } from "@common/api/apiShared.ts";
import { ITransferResponsibilityDialog } from "./TransferResponsibilityDialog";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { UserResponsibilityEntityType } from "../../reviewUserResponsibility/ReviewUserResponsibility.type";
import { useFeatureFlags } from "../../../auth/planProvider/PlanProvider";
import { FeatureFlag } from "../../../auth/planProvider/PlanProvider.types";

function useTransferResponsibilityDialogHooks(props: ITransferResponsibilityDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const featureFlags = useFeatureFlags();

    const [transferModel, setTransferModel] = useState<TransferUserResponsibilityModel>({
        fromUser: props.fromUserId ?? null,
        toUser: null,
        transferTasks: props.userResponsibilityEntityType === UserResponsibilityEntityType.Task ? true : false,
        transferAnnualWheelActivities: props.userResponsibilityEntityType === UserResponsibilityEntityType.AnnualWheelActivity ? true : false,
        transferProcessingActivities: props.userResponsibilityEntityType === UserResponsibilityEntityType.ProcessingActivity ? true : false,
        transferSystems: props.userResponsibilityEntityType === UserResponsibilityEntityType.System ? true : false,
        transferContractDocuments: props.userResponsibilityEntityType === UserResponsibilityEntityType.ContractDocument ? true : false,
        transferSharings: props.userResponsibilityEntityType === UserResponsibilityEntityType.Sharing ? true : false,
        transferLegalEntityAudits: props.userResponsibilityEntityType === UserResponsibilityEntityType.LegalEntityAudit ? true : false,
        transferAuditGroups: props.userResponsibilityEntityType === UserResponsibilityEntityType.LegalEntityAuditGroup ? true : false,
        transferInternalAssessments: props.userResponsibilityEntityType === UserResponsibilityEntityType.InternalAssessment ? true : false,
        transferPublicInternalAssessmentTemplates:
            props.userResponsibilityEntityType === UserResponsibilityEntityType.PublicInternalAssessmentTemplate ? true : false,
        transferLegalEntities: props.userResponsibilityEntityType === UserResponsibilityEntityType.LegalEntity ? true : false,
        transferContracts: props.userResponsibilityEntityType === UserResponsibilityEntityType.Contract ? true : false,
        transferFrameworks: props.userResponsibilityEntityType === UserResponsibilityEntityType.Framework ? true : false,
        transferPolicies: props.userResponsibilityEntityType === UserResponsibilityEntityType.Policy ? true : false,
        transferLegalEntityDocuments: props.userResponsibilityEntityType === UserResponsibilityEntityType.LegalEntityDocument ? true : false,
        transferObligations: props.userResponsibilityEntityType === UserResponsibilityEntityType.Obligation ? true : false,
        transferRiskMitigationPlans: props.userResponsibilityEntityType === UserResponsibilityEntityType.RiskMitigationPlan ? true : false,
        transferRiskAreas: props.userResponsibilityEntityType === UserResponsibilityEntityType.RiskArea ? true : false,
        transferInternalAssessmentGroups: props.userResponsibilityEntityType === UserResponsibilityEntityType.InternalAssessmentGroup ? true : false,
        transferIncidentLogEntries: props.userResponsibilityEntityType === UserResponsibilityEntityType.IncidentLogEntry ? true : false,
        transferProcesses: props.userResponsibilityEntityType === UserResponsibilityEntityType.Process ? true : false,
    });

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: () => {
            props.onTransferComplete?.();
            props.onDialogClose();
            snackbar.show(translateString("responsibilityTransfered"));
        },
    });

    const transferResponsible = async () => {
        await saveMutation.mutateAsync(transferModel);
    };

    function save(model: TransferUserResponsibilityModel) {
        return post<{}>("/Users/transferUserResponsibility", model);
    }

    let responsiblesQuery = useResponsibleQuery();

    let fromUsers;
    let toUsers;
    if (responsiblesQuery.userData) {
        fromUsers = responsiblesQuery
            .data(transferModel.fromUser)
            ?.filter((x) => x.id !== transferModel.toUser)
            .map((d) => {
                return d;
            });

        toUsers = responsiblesQuery
            .data(transferModel.toUser)
            ?.filter((x) => x.id !== transferModel.fromUser)
            .map((d) => {
                return d;
            });
    }

    const onUserChange = (property: "fromUser" | "toUser", value: string | null) => {
        setTransferModel({ ...transferModel, [property]: value });
    };

    const onTransferChange = (property: keyof TransferUserResponsibilityModel, checked: boolean) => {
        setTransferModel({ ...transferModel, [property]: checked });
    };

    const transferButtonEnabled =
        transferModel.fromUser !== null &&
        transferModel.toUser !== null &&
        (transferModel.transferTasks ||
            transferModel.transferProcessingActivities ||
            transferModel.transferSystems ||
            transferModel.transferContractDocuments ||
            transferModel.transferSharings ||
            transferModel.transferLegalEntityAudits ||
            transferModel.transferAuditGroups ||
            transferModel.transferInternalAssessments ||
            transferModel.transferPublicInternalAssessmentTemplates ||
            transferModel.transferLegalEntities ||
            transferModel.transferContracts ||
            transferModel.transferFrameworks ||
            transferModel.transferPolicies ||
            transferModel.transferAnnualWheelActivities ||
            transferModel.transferLegalEntityDocuments ||
            transferModel.transferObligations ||
            transferModel.transferRiskMitigationPlans ||
            transferModel.transferRiskAreas ||
            transferModel.transferInternalAssessmentGroups ||
            transferModel.transferIncidentLogEntries ||
            transferModel.transferProcesses);

    function isTransferCheckboxVisible(entityType: UserResponsibilityEntityType) {
        return entityType !== UserResponsibilityEntityType.Process || featureFlags.isEnabled(FeatureFlag.ProcessTemplates);
    }

    return {
        fromUsers,
        toUsers,
        onUserChange,
        onTransferChange,
        transferModel,
        transferResponsible,
        transferButtonEnabled,
        inProgress: saveMutation.isLoading,
        isTransferCheckboxVisible,
    };
}

export default useTransferResponsibilityDialogHooks;
